import { RedRed60, SequenceMenu } from '@sequencehq/core-components'
import { FC, useCallback, useState } from 'react'
import { useNotifications, SimpleModal } from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

export const ArchiveWorkflowModal: FC<{
  automationId: string
  versionId: string
  onSuccess?: () => void
  onClose: () => void
}> = ({ automationId, versionId, onSuccess, onClose }) => {
  const [isArchiving, setIsArchiving] = useState(false)
  const { displayNotification } = useNotifications()

  const automationVersionQuery = useQuery(
    dashboardv99990101Client.getAutomationVersion,
    { automationId, versionId }
  )

  const archiveWorkflow = useCallback(async () => {
    if (automationVersionQuery.data?.active) {
      const deactivationRes =
        await dashboardv99990101Client.postDeactivateAutomationVersion({
          automationId,
          versionId
        })

      if (deactivationRes.error) {
        displayNotification('Failed to archive workflow', {
          type: 'error'
        })
        return false
      }
    }

    const archiveRes = await dashboardv99990101Client.postArchiveAutomation({
      id: automationId
    })

    if (archiveRes.error) {
      displayNotification('Failed to archive workflow', {
        type: 'error'
      })
      return false
    }

    displayNotification('Workflow archived', { type: 'success' })

    if (onSuccess) {
      onSuccess()
    }

    return Boolean(!archiveRes.error)
  }, [
    automationId,
    versionId,
    displayNotification,
    onSuccess,
    automationVersionQuery.data?.active
  ])

  return (
    <SimpleModal
      isLoading={automationVersionQuery.isPending}
      isOpen
      onClose={onClose}
      variant="dialog"
      title="Archive workflow"
      submitButtonText="Archive"
      submittingButtonText="Archiving..."
      submitting={isArchiving}
      isDestructive
      onSubmit={async () => {
        setIsArchiving(true)
        const result = await archiveWorkflow()
        setIsArchiving(false)
        return result
      }}
    >
      Are you sure you want to archive this workflow?
    </SimpleModal>
  )
}

export const ArchiveWorkflowMenuItem: FC<{
  automationId: string
  versionId: string
  onSuccess?: () => void
}> = ({ automationId, versionId, onSuccess }) => {
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <SequenceMenu.Item
        key="archive-workflow"
        uuid="archive-workflow"
        label="Archive workflow"
        data-testid="automations.editor.kebabMenu.archive"
        style={{ color: RedRed60 }}
        onClick={() => {
          setShowModal(true)
        }}
      />
      {showModal && (
        <ArchiveWorkflowModal
          automationId={automationId}
          versionId={versionId}
          onSuccess={onSuccess}
          onClose={() => {
            setShowModal(false)
          }}
        />
      )}
    </>
  )
}
